/* One kind of credit movement, newest first, a page at a time: payments (grants) in
   Billing, usage in Usage. A paid grant links the provider's invoice. */
import { useState } from "react";
import { STATEMENT_PAGE, useStatement } from "@/shared/api/billing";
import { ExternalLink } from "@/shared/components/ExternalLink";
import { Group, GhostButton, RowList } from "@/shared/components/settings-atoms";
import { formatCredits, shortDate } from "./format";

import type { Amount, StatementKind } from "@/shared/types/billing";

/* Date · description · credits · balance · invoice link. */
const COLS = "grid grid-cols-[110px_minmax(0,1fr)_96px_96px_72px] items-center gap-3";

const signed = (delta: Amount) => `${Number(delta) > 0 ? "+" : ""}${formatCredits(delta)}`;

// Billing names a usage line by nothing yet, so a blank description reads as its kind.
const KIND_LABEL: Record<StatementKind, string> = { grant: "Credits added", usage: "Agent usage", void: "Refund", adjust: "Adjustment" };

export default function StatementGroup({ kind, title, empty }: { kind: StatementKind; title: string; empty: string }) {
  const [offset, setOffset] = useState(0);
  const { data, isPending, isError } = useStatement(kind, offset);
  const total = data?.total ?? 0;

  return (
    <Group title={title}>
      {isPending ? (
        <RowList><div className="py-4 text-[12.5px] text-[var(--text-quaternary)] italic">Loading…</div></RowList>
      ) : isError ? (
        <RowList><div className="py-4 text-[12.5px] text-[var(--text-quaternary)] italic">{title} could not be read.</div></RowList>
      ) : !data.items.length ? (
        <RowList><div className="py-4 text-[12.5px] text-[var(--text-quaternary)] italic">{empty}</div></RowList>
      ) : (
        <>
          {/* One grid for the header strip and the rows; a phone scrolls it sideways. */}
          <div className="overflow-x-auto no-scrollbar rounded-[18px]">
            <RowList
              className="min-w-[560px]"
              header={
                <div className={`${COLS} flex-1`}>
                  <span>Date</span>
                  <span>Description</span>
                  <span className="text-right">Credits</span>
                  <span className="text-right">Balance</span>
                  <span />
                </div>
              }
            >
              {data.items.map((line) => (
                <div key={line.id} className={`${COLS} py-3 text-[13px] whitespace-nowrap`}>
                  <span className="tabular-nums text-[var(--text-secondary)]">{shortDate(line.created_at)}</span>
                  <span className="truncate text-[var(--text-primary)]">{line.description ?? KIND_LABEL[line.kind]}</span>
                  <span className={`text-right font-mono tabular-nums ${Number(line.delta) > 0 ? "text-[var(--success)]" : "text-[var(--text-secondary)]"}`}>
                    {signed(line.delta)}
                  </span>
                  <span className="text-right font-mono tabular-nums text-[var(--text-secondary)]">{formatCredits(line.balance_after)}</span>
                  <span className="text-right">
                    {line.payment?.invoice_url && <ExternalLink href={line.payment.invoice_url}>Invoice</ExternalLink>}
                  </span>
                </div>
              ))}
            </RowList>
          </div>
          {total > STATEMENT_PAGE && (
            <div className="mt-2 flex items-center justify-end gap-2 text-[12px] text-[var(--text-tertiary)]">
              <span className="font-mono tabular-nums">
                {offset + 1}–{Math.min(offset + STATEMENT_PAGE, total)} of {total}
              </span>
              <GhostButton disabled={offset === 0} onClick={() => setOffset(offset - STATEMENT_PAGE)} className="disabled:opacity-40 disabled:pointer-events-none">
                Previous
              </GhostButton>
              <GhostButton disabled={offset + STATEMENT_PAGE >= total} onClick={() => setOffset(offset + STATEMENT_PAGE)} className="disabled:opacity-40 disabled:pointer-events-none">
                Next
              </GhostButton>
            </div>
          )}
        </>
      )}
    </Group>
  );
}
